import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PATH_DASHBOARD } from "../../routes/paths";
import axiosInstance from "../../utils/axios";

const initialState = {
  fetchLoading: false,
  isLoading: false,
  orders: [],
  userOrders: [],
  userNoteHistory: [],
  singleOrder: null,
};

export const createOrder = createAsyncThunk(
  "createOrder/order",
  async ({ data, enqueueSnackbar, navigate }, thunkApi) => {
    try {
      const response = await axiosInstance.post("orders", data);
      enqueueSnackbar("Order is added successfully");
      navigate && navigate(PATH_DASHBOARD.orders.list);
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const createUserNote = createAsyncThunk(
  "userNote/create",
  async ({ user_id, note, enqueueSnackbar }, thunkApi) => {
    try {
      const response = await axiosInstance.post("followup-notes", {
        user_id,
        note,
      });
      enqueueSnackbar("Note is added successfully");
      return response.data;
    } catch (error) {
      return thunkApi.rejectWithValue(
        error.response?.data?.message || "Failed to create note"
      );
    }
  }
);

export const fetchOrders = createAsyncThunk(
  "fetchOrders/order",
  async (
    { enqueueSnackbar, limit = 10, page = 0, status, date, ...params },
    thunkApi
  ) => {
    try {
      const response = await axiosInstance.get(`orders`, {
        params: {
          limit,
          page: page + 1,
          status,
          ...params,
        },
      });

      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

// fetch user order
export const fetchUserOrder = createAsyncThunk(
  "fetchUserOrder/order",
  async ({ enqueueSnackbar, limit = 10, page = 0, status, id }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`customer/${id}/orders`, {
        params: {
          limit,
          page: page + 1,
          status,
        },
      });

      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

// fetch user note history
export const fetchUserNoteHistory = createAsyncThunk(
  "fetchUserNoteHistory/order",
  async ({ enqueueSnackbar, limit = 10, page = 0, status, id }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`customer/${id}/note`, {
        params: {
          limit,
          page: page + 1,
          status,
        },
      });

      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

// fetch riders order

export const fetchRidersOrder = createAsyncThunk(
  "fetchRidersOrders/order",
  async ({ enqueueSnackbar, limit = 10, page = 0, status, date }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`rider/orders`, {
        params: {
          limit,
          page: page + 1,
          status,
          from_date: date,
        },
      });

      return {
        data: response.data.data.data,
        meta: {
          total: response.data.data.total,
        },
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const fetchSingleOrder = createAsyncThunk(
  "fetchSingleOrder/order",
  async ({ enqueueSnackbar, slug }, thunkApi) => {
    try {
      const response = await axiosInstance.get(`orders/${slug}`);

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateOrder = createAsyncThunk(
  "updateOrder/order",
  async ({ slug, data, enqueueSnackbar, limit, page, ...params }, thunkApi) => {
    try {
      const response =
        data instanceof FormData
          ? await axiosInstance.post(`orders/${slug}`, data)
          : await axiosInstance.patch(`orders/${slug}`, data);
      thunkApi.dispatch(
        fetchOrders({
            limit,
            page,
            enqueueSnackbar,
            ...params,
          })
      );
      return {
        data: response.data.data,
        enqueueSnackbar,
        slug,
      };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateOrderItemStatus = createAsyncThunk(
  "updateOrderItemStatus/order",
  async (
    { id, data, enqueueSnackbar, limit, page, filterStatus, ...params },
    thunkApi
  ) => {
    try {
      const response = await axiosInstance.patch(`ordered-items/${id}`, data);
      thunkApi.dispatch(
        fetchOrders({ enqueueSnackbar, limit, page, status: filterStatus, ...params })
      );

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateOrderStatus = createAsyncThunk(
  "updateOrderStatus/order",
  async (
    { slug, data, enqueueSnackbar, limit, page, filterStatus, ...params },
    thunkApi
  ) => {
    try {
      const response = await axiosInstance.patch(`orders/${slug}`, data);
      thunkApi.dispatch(
        fetchOrders({ enqueueSnackbar, limit, page, status: filterStatus, ...params })
      );

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const updateOrderPaymentStatus = createAsyncThunk(
  "updateOrderPaymentStatus/order",
  async (
    { id, data, enqueueSnackbar, limit, page, filterStatus, ...params },
    thunkApi
  ) => {
    try {
      const response = await axiosInstance.patch(`orders/${id}`, data);
      thunkApi.dispatch(
        fetchOrders({ enqueueSnackbar, limit, page, status: filterStatus, ...params })
      );

      return response.data.data;
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

export const deleteOrder = createAsyncThunk(
  "deleteOrder/order",
  async ({ id, enqueueSnackbar }, thunkApi) => {
    try {
      await axiosInstance.delete(`orders/${id}`);
      return { id, enqueueSnackbar };
    } catch (error) {
      return thunkApi.rejectWithValue({ error, enqueueSnackbar });
    }
  }
);

const orderSlice = createSlice({
  name: "order",
  initialState,
  extraReducers: (builder) => {
    // TODO: create order
    builder.addCase(createOrder.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(createOrder.fulfilled, (state) => {
      state.isLoading = false;
    });

    builder.addCase(createOrder.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: fetch product
    builder.addCase(fetchOrders.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchOrders.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.orders = action.payload;
    });

    builder.addCase(fetchOrders.rejected, (state, action) => {
      state.fetchLoading = false;
      const { enqueueSnackbar, error } = action.payload || {};
      if (enqueueSnackbar) {
        enqueueSnackbar(error?.message || "Failed to fetch orders", {
          variant: "error",
        });
      }
    });

    // ==========================

    // TODO: fetch single product
    builder.addCase(fetchSingleOrder.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchSingleOrder.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.singleOrder = action.payload;
    });

    builder.addCase(fetchSingleOrder.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: update order
    builder.addCase(updateOrder.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(updateOrder.fulfilled, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar("Order is updated successfully");
      state.orders.data = state.orders.data.map((order) =>
        order.code === action.payload.slug ? action.payload.data : order
      );
      if (action.payload?.setOpenEdit) {
        action.payload?.setOpenEdit(false);
      }
    });

    builder.addCase(updateOrder.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // ==========================

    // TODO: delete product
    builder.addCase(deleteOrder.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(deleteOrder.fulfilled, (state, action) => {
      state.isLoading = false;
      state.orders.data = state.orders.data.filter(
        (order) => order.id !== action.payload.id
      );
      action.payload.enqueueSnackbar("Order is deleted successfully");
    });

    builder.addCase(deleteOrder.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // fetch user notes
    builder.addCase(fetchUserNoteHistory.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchUserNoteHistory.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.userNoteHistory = action.payload;
    });

    builder.addCase(fetchUserNoteHistory.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    builder.addCase(createUserNote.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(createUserNote.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(createUserNote.rejected, (state, action) => {
      state.isLoading = false;
      // if (action.payload) {
      //   enqueueSnackbar(action.payload, { variant: "error" });
      // }
    });

    // ==========================
    // fetch user orders
    builder.addCase(fetchUserOrder.pending, (state, _) => {
      state.fetchLoading = true;
    });

    builder.addCase(fetchUserOrder.fulfilled, (state, action) => {
      state.fetchLoading = false;
      state.userOrders = action.payload;
    });

    builder.addCase(fetchUserOrder.rejected, (state, action) => {
      state.fetchLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    // TODO: fetch product
    builder.addCase(updateOrderStatus.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(updateOrderStatus.fulfilled, (state, action) => {
      state.isLoading = false;
      state.orders.data = state.orders.data.map((order) =>
        order.code === action.payload.slug ? action.payload : order
      );
    });

    builder.addCase(updateOrderStatus.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });

    builder.addCase(updateOrderItemStatus.pending, (state, _) => {
      state.isLoading = true;
    });

    builder.addCase(updateOrderItemStatus.fulfilled, (state, action) => {
      state.isLoading = false;
      // We rely on re-fetching orders to update the state, but we could update optimistically here if needed.
    });

    builder.addCase(updateOrderItemStatus.rejected, (state, action) => {
      state.isLoading = false;
      action.payload.enqueueSnackbar(action.payload.error.message, {
        variant: "error",
      });
    });
  },
});

export default orderSlice.reducer;
